import { GuideSourceFormat } from "../types";
import { convertFileToMarkdown } from "../api/client";
import { OPERATOR_DOCUMENT_ACCEPT } from "./operatorDocuments";

export const MAX_GUIDE_FILE_BYTES = 10 * 1024 * 1024;
export const MAX_GUIDE_CHARS = 120_000;
export const GUIDE_FILE_ACCEPT = OPERATOR_DOCUMENT_ACCEPT;

const TEXT_EXTENSIONS = new Set([
  "txt",
  "md",
  "log",
  "json",
  "csv",
  "yaml",
  "yml",
  "text",
]);

export interface GuideFileReadResult {
  text: string;
  fileName: string;
  mimeType: string;
  sourceFormat: GuideSourceFormat;
  truncated: boolean;
  title: string | null;
}

function extensionOf(name: string): string {
  const idx = name.lastIndexOf(".");
  return idx >= 0 ? name.slice(idx + 1).toLowerCase() : "";
}

function truncate(text: string): { text: string; truncated: boolean } {
  if (text.length <= MAX_GUIDE_CHARS) {
    return { text, truncated: false };
  }
  return { text: text.slice(0, MAX_GUIDE_CHARS), truncated: true };
}

export function fileRelativePath(file: File): string {
  const relative = (file as File & { webkitRelativePath?: string }).webkitRelativePath;
  return relative?.trim() || file.name;
}

export function isJunkUploadFile(file: File): boolean {
  const name = file.name;
  const relative = fileRelativePath(file);
  if (!name || name.startsWith(".") || name === "Thumbs.db") return true;
  if (relative.includes("__MACOSX")) return true;
  if (file.size === 0) return true;
  return false;
}

export function inferDocumentName(
  fileName: string,
  markdown: string,
  convertTitle?: string | null,
): string {
  if (convertTitle?.trim()) {
    return convertTitle.trim().slice(0, 255);
  }
  const heading = markdown.match(/^#{1,2}\s+(.+)$/m);
  if (heading?.[1]?.trim()) {
    return heading[1].trim().slice(0, 255);
  }
  const base = fileName.replace(/\.[^.]+$/, "").replace(/[_]+/g, " ").trim();
  return (base || fileName).slice(0, 255);
}

export function inferDocumentDescription(markdown: string, fileName: string): string {
  const withoutHeadings = markdown
    .replace(/^#{1,6}\s+.*$/gm, "")
    .replace(/```[\s\S]*?```/g, "")
    .trim();
  const paragraph = withoutHeadings
    .split(/\n\s*\n/)
    .map((p) => p.replace(/\s+/g, " ").trim())
    .find((p) => p.length > 0);
  if (paragraph) {
    return paragraph.length > 240 ? `${paragraph.slice(0, 237).trimEnd()}…` : paragraph;
  }
  return `Imported from ${fileName}`;
}

export function inferOperatorEntityClass(
  fileName: string,
  relativePath?: string,
): "sop" | "runbook" | "help-guide" {
  const haystack = `${relativePath ?? ""} ${fileName}`.toLowerCase();
  if (/\brunbook/.test(haystack)) return "runbook";
  if (/\bhelp[- ]?guide\b|\bhelpguide\b/.test(haystack)) return "help-guide";
  if (/\bsop\b/.test(haystack)) return "sop";
  return "sop";
}

export async function readGuideFile(file: File): Promise<GuideFileReadResult> {
  if (file.size > MAX_GUIDE_FILE_BYTES) {
    throw new Error(`File exceeds ${MAX_GUIDE_FILE_BYTES / (1024 * 1024)} MB limit`);
  }

  const ext = extensionOf(file.name);
  let text = "";
  let sourceFormat = GuideSourceFormat.TEXT;
  let title: string | null = null;

  if (TEXT_EXTENSIONS.has(ext)) {
    sourceFormat =
      ext === "md" ? GuideSourceFormat.MARKDOWN : GuideSourceFormat.TEXT;
    text = await file.text();
  } else {
    const converted = await convertFileToMarkdown(file);
    text = converted.markdown;
    title = converted.title ?? null;
    sourceFormat = GuideSourceFormat.MARKDOWN;
  }

  if (!text.trim()) {
    throw new Error("File produced empty Markdown. Conversion failed or the file has no extractable text.");
  }

  const clipped = truncate(text);
  return {
    text: clipped.text,
    fileName: file.name,
    mimeType: file.type || "application/octet-stream",
    sourceFormat,
    truncated: clipped.truncated,
    title,
  };
}

export function buildGuideAnalyzeMessage(result: GuideFileReadResult): string {
  const payload = JSON.stringify({
    fileName: result.fileName,
    sourceFormat: result.sourceFormat,
    text: result.text,
  });
  return `Analyze this SOP/error guide and extract all operational errors.

\`\`\`sop-guide-import
${payload}
\`\`\``;
}

export function buildIntakeRequirementMessage(
  requirement: string,
  files: GuideFileReadResult[] = [],
): string {
  const trimmed = requirement.trim();
  if (files.length === 0) {
    return trimmed;
  }
  const docs = files
    .map((f) => `### ${f.fileName}\n\n${f.text}`)
    .join("\n\n");
  return `${trimmed}\n\n---\nRelated documents (Markdown):\n\n${docs}`;
}

export function buildProductionSupportTriageMessage(
  goal: string,
  files: GuideFileReadResult[] = [],
): string {
  const payload = {
    goal: goal.trim(),
    files: files.map((f) => ({
      fileName: f.fileName,
      sourceFormat: f.sourceFormat,
      markdown: f.text,
    })),
  };
  return `${goal.trim()}

\`\`\`production-support-triage
${JSON.stringify(payload)}
\`\`\``;
}
